// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: what the user has open while they talk to the floating voice assistant,
// so "reply saying Friday works" or "what is he asking?" act on it without a
// search. Ids plus the few lines visible on screen; the assistant reads the full
// thread with its own tools when it needs more. Posted alongside each spoken turn,
// in parallel with the transcription, and taken by the next turn on the server
// (KonvoScreenContext). On Home the Home context (rows, selected item) goes instead.
import {Routes} from '@app/app/Routes';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import Guilds from '@app/features/guild/state/Guilds';
import Agents from '@app/features/konvo_agents/state/Agents';
import {isWorking, parseAgentsPath} from '@app/features/konvo_agents/state/AgentsModel';
import KonvoTeamsGroups from '@app/features/konvo_connections/state/KonvoTeamsGroups';
import KonvoMail from '@app/features/konvo_mail/state/KonvoMail';
import KonvoTasks from '@app/features/konvo_tasks/state/KonvoTasks';
import HomeContext from '@app/features/konvo_today/state/HomeContext';
import KonvoWhatsApp, {mediaLabel} from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import Navigation from '@app/features/navigation/state/Navigation';
import {http} from '@app/features/platform/transport/RestTransport';
import Users from '@app/features/user/state/Users';
import type {AgentThreadSummary} from '@fluxer/schema/src/domains/konvo/Agents';

export interface ScreenContextBody {
	source: 'konvo' | 'teams' | 'whatsapp' | 'mail' | 'clickup' | 'agents';
	title: string;
	conversation_id?: string;
	message_id?: string;
	task_id?: string;
	from?: string;
	status?: string;
	body?: string;
	messages?: Array<{from: string; text: string}>;
}

const MAX_MESSAGES = 8;
const MAX_BODY_CHARS = 800;

function plainText(html: string): string {
	return html
		.replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/\s+/g, ' ')
		.trim();
}

function openTask(): ScreenContextBody | null {
	const taskId = KonvoTasks.openTaskId;
	const task = taskId ? KonvoTasks.getTask(taskId) : null;
	if (!taskId || !task) return null;
	return {
		source: 'clickup',
		title: task.name,
		task_id: taskId,
		status: task.status?.status ?? undefined,
		from: task.list?.name ?? undefined,
		body: task.description ? task.description.slice(0, MAX_BODY_CHARS) : undefined,
	};
}

function openMail(): ScreenContextBody | null {
	const id = KonvoMail.selectedId;
	const summary = KonvoMail.selectedSummary;
	const detail = KonvoMail.selectedDetail;
	if (!id || (!summary && !detail)) return null;
	const subject = summary?.subject ?? '';
	const sender = summary ? [summary.from_name, summary.from_address && `<${summary.from_address}>`] : [];
	const body = detail ? (detail.body_type === 'html' ? plainText(detail.body) : detail.body) : (summary?.preview ?? '');
	return {
		source: 'mail',
		title: subject || '(no subject)',
		message_id: id,
		from: sender.filter(Boolean).join(' ') || undefined,
		body: body.replace(/\s+/g, ' ').trim().slice(0, MAX_BODY_CHARS) || undefined,
	};
}

function openWhatsApp(jid: string): ScreenContextBody | null {
	const chat = KonvoWhatsApp.chats.find((entry) => entry.jid === jid);
	const messages = (KonvoWhatsApp.messages.get(jid) ?? []).slice(-MAX_MESSAGES).map((message) => ({
		from: message.from_me ? 'You' : message.sender_name || chat?.name || 'Them',
		text: message.text || mediaLabel(message.media_kind, message.media_name, message.media_gif),
	}));
	return {
		source: 'whatsapp',
		title: chat?.name || jid.split('@')[0],
		conversation_id: `whatsapp:${jid}`,
		messages,
	};
}

function openChannel(channelId: string): ScreenContextBody | null {
	const channel = Channels.getChannel(channelId);
	// The Assistant DM is the conversation itself.
	if (!channel || ChannelUtils.isSystemDmChannel(channel)) return null;
	const teams = KonvoTeamsGroups.linkFor(channel.id);
	const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : undefined;
	const title = channel.guildId
		? `#${channel.name ?? 'channel'}${guild?.name ? ` in ${guild.name}` : ''}`
		: ChannelUtils.getDMDisplayName(channel);
	const me = Users.getCurrentUser()?.id;
	const messages = (ChannelMessages.get(channel.id)?.toArray() ?? [])
		.filter((message) => message.content.trim())
		.slice(-MAX_MESSAGES)
		.map((message) => ({
			from: message.author.id === me ? 'You' : message.author.displayName,
			text: message.content,
		}));
	return {
		source: teams ? 'teams' : 'konvo',
		title,
		conversation_id: teams?.chat_id ? `teams:${encodeURIComponent(teams.chat_id)}` : `konvo:${channel.id}`,
		messages,
	};
}

/** A thread's state in a few words, as the list shows it. */
function agentStatus(thread: AgentThreadSummary): string {
	if (thread.needsYou)
		return thread.waitingOn === 'question'
			? 'Asks you a question'
			: thread.waitingOn === 'plan'
				? 'Wants you to approve its plan'
				: 'Wants your approval';
	if (isWorking(thread)) return 'Working';
	if (thread.status === 'error') return 'Failed';
	if (thread.status === 'interrupted') return 'Stopped';
	return thread.settled ? 'Settled' : 'Done';
}

/** The Agents rows in view, top to bottom (what "the second one" or "the one at the top" means). */
function agentRowsInView(): Array<{from: string; text: string}> {
	const rows: Array<{from: string; text: string}> = [];
	if (typeof document === 'undefined') return rows;
	for (const element of document.querySelectorAll<HTMLElement>('[data-agent-thread]')) {
		const rect = element.getBoundingClientRect();
		if (rect.height === 0 || rect.bottom < 0 || rect.top > window.innerHeight) continue;
		const [machineId, threadId] = (element.dataset.agentThread ?? '').split('/');
		const thread = machineId && threadId ? Agents.summary(machineId, threadId) : null;
		if (!thread) continue;
		rows.push({from: agentStatus(thread), text: `${thread.title} (thread_id=${thread.id}, machine_id=${machineId})`});
		if (rows.length >= MAX_MESSAGES) break;
	}
	return rows;
}

/** The Agents page: the thread that is open (with what it waits on) and the list rows in view. */
function openAgents(pathname: string): ScreenContextBody | null {
	const here = parseAgentsPath(pathname);
	const rows = agentRowsInView();
	if (!here.machineId || !here.threadId) {
		const machineId = here.machineId ?? Agents.listMachineId;
		const machine = Agents.machine(machineId);
		return {
			source: 'agents',
			title: machine ? `Agents on ${machine.name}` : 'Agents',
			conversation_id: machineId ? `agents:${machineId}` : undefined,
			messages: rows,
		};
	}
	const detail = Agents.thread(here.machineId, here.threadId)?.detail ?? null;
	const thread = detail ?? Agents.summary(here.machineId, here.threadId);
	if (!thread) return null;
	const approval = detail?.approvals[0];
	const question = detail?.questions?.[0]?.questions[0];
	const waiting = approval
		? `approval request_id=${approval.requestId} (${approval.kind}): ${approval.detail}`
		: question
			? `question request_id=${detail?.questions?.[0]?.requestId}: ${question.question}${question.options.length ? ` Options: ${question.options.map((option, index) => `${index + 1}. ${option.label}`).join('; ')}` : ''}`
			: undefined;
	return {
		source: 'agents',
		title: thread.title || 'Untitled thread',
		conversation_id: `agents:${here.machineId}/${here.threadId}`,
		status: agentStatus(thread),
		body: waiting?.slice(0, MAX_BODY_CHARS),
		messages: rows,
	};
}

/** What is open right now, most specific first: a task card on top of anything, then the page's own content. */
export function describeScreen(): ScreenContextBody | null {
	const task = openTask();
	if (task) return task;
	const path = Navigation.pathname;
	if (Routes.isAgentsRoute(path)) return openAgents(path);
	if (Routes.isDMMailRoute(path) || Routes.isEmailsRoute(path)) return openMail();
	const jid = Routes.dmWhatsAppJid(path) ?? (Routes.isWhatsAppRoute(path) ? KonvoWhatsApp.selectedJid : null);
	if (jid) return openWhatsApp(jid);
	const channelId = Navigation.channelId;
	return channelId ? openChannel(channelId) : null;
}

/** On Home the Home context goes up instead of a screen description. */
export function screenGoesToHome(): boolean {
	return Routes.isTodayRoute(Navigation.pathname);
}

/**
 * Tell the server what the next spoken turn is about. Never throws. Sends an empty
 * body when nothing is open, which clears anything left from an earlier turn.
 */
export async function pushScreenContext(): Promise<void> {
	try {
		if (screenGoesToHome()) {
			await HomeContext.push();
			return;
		}
		await http.post(Endpoints.KONVO_ASSISTANT_VOICE_SCREEN, {body: describeScreen() ?? {}});
	} catch {
		// The turn still works without it.
	}
}
